import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { PlaybookTestSpecExpected } from "@repo/app-types/entities/teammateAgent";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { Size } from "@repo/ui/helpers/helpers";
import { useMemo, useState, type FC } from "react";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { PlaybookTestSpecExpectedOutcomeSelector } from "./playbookTestSpecExpectedOutcomeSelector.js";

export interface PlaybookTestSpecDraft {
  name: string;
  description: string;
  expected: PlaybookTestSpecExpected;
}

interface PlaybookTestSpecEditorSlideoverProps {
  playbookId: PlaybookId;
  existingTestSpec?: {
    id: string;
    name: string;
    description: string;
    expected: PlaybookTestSpecExpected;
  };
  onClose: () => void;
  onSubmit: (draft: PlaybookTestSpecDraft, testSpecId?: string) => void;
}

export const PlaybookTestSpecEditorSlideover: FC<
  PlaybookTestSpecEditorSlideoverProps
> = ({ playbookId, existingTestSpec, onClose, onSubmit }) => {
  const { data: playbook, isLoading: isLoadingPlaybook } =
    usePlaybookByIdQuery(playbookId);

  const [draft, setDraft] = useState<PlaybookTestSpecDraft>({
    name: existingTestSpec?.name ?? "",
    description: existingTestSpec?.description ?? "",
    expected: existingTestSpec?.expected ?? {
      nodes: {},
    },
  });

  const isSubmitDisabled = useMemo(() => {
    const hasInvalidName = draft.name.trim() === "";
    const hasInvalidDescription = draft.description.trim() === "";

    // When editing, require at least one modification
    if (existingTestSpec) {
      const hasNoChanges =
        draft.name === existingTestSpec.name &&
        draft.description === existingTestSpec.description &&
        JSON.stringify(draft.expected) ===
          JSON.stringify(existingTestSpec.expected);

      if (hasNoChanges) {
        return true;
      }
    }

    return hasInvalidName || hasInvalidDescription;
  }, [draft, existingTestSpec]);

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      return;
    }

    onSubmit(
      {
        name: draft.name.trim(),
        description: draft.description.trim(),
        expected: draft.expected,
      },
      existingTestSpec?.id,
    );
    onClose();
  };

  const isEditing = !!existingTestSpec;
  const title = isEditing ? "Edit test case" : "Create test case";

  return (
    <SlideoverDialog label={title}>
      <SlideoverDialogTitleHeader isLoading={isLoadingPlaybook}>
        {title}
      </SlideoverDialogTitleHeader>
      <SlideoverDialogContent>
        <div className="space-y-4 p-4">
          <Input
            label="Name"
            value={draft.name}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, name: event.target.value }))
            }
            placeholder="Upset customer"
            maxLength={100}
          />
          <Textarea
            label="Customer description"
            value={draft.description}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, description: value }))
            }
            placeholder="A customer who is upset and wants to talk to a representative immediately."
            description="Describe customer behavior that you want to test your agent's performance on."
          />
          {!playbook ? (
            <div className="text-sm text-muted-foreground">
              Playbook is loading...
            </div>
          ) : (
            <>
              <div>
                <Label size={Size.SMALL}>Expected outcome</Label>
                <div className="mt-2 space-y-4">
                  <p className="mb-3 text-sm text-muted-foreground">
                    Pick the expected outcome for this test case. Select
                    &ldquo;None&rdquo; if you don&apos;t want the outcome to be
                    tested .
                  </p>
                  <PlaybookTestSpecExpectedOutcomeSelector
                    playbook={playbook}
                    expected={draft.expected}
                    onChange={(expected) =>
                      setDraft((prev) => ({ ...prev, expected }))
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </SlideoverDialogContent>
      <SlideoverDialogButtons>
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleSubmit}
          isDisabled={isSubmitDisabled || isLoadingPlaybook}
        >
          {isEditing ? "Save test case" : "Create test case"}
        </Button>
      </SlideoverDialogButtons>
    </SlideoverDialog>
  );
};
